import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import DOMPurify from 'dompurify';
import {
    FaChevronDown, FaChevronUp, FaPlus, FaTrash, FaShieldAlt, FaAward
} from "react-icons/fa";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import styles from "./SifatSiyosati.module.css";
import { testUrl } from "../home/BaseData";

const SifatSiyosati = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [activeIndex, setActiveIndex] = useState(null);
    const [policyData, setPolicyData] = useState([]);
    const [introData, setIntroData] = useState({ content_uz: "" });
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [presentations, setPresentations] = useState([]);
    const [selectedImage, setSelectedImage] = useState(null);


    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, false] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
            ['link', 'clean']
        ],
    };

    const initialSections = [
        {
            title: "Umumiy qoidalar",
            content: `<p>1.1. Renessans taʼlim universitetining ichki taʼlim sifatini taʼminlash tizimi taʼlim sifatini oshirish va sifatni yaxshilash madaniyatini rivojlantirish uchun Universitetda tashkil etilgan umumiy yondashuvlar, siyosatlar, asosiy tamoyillar, standartlar va asosiy mexanizmlarni aks ettiradi.</p><p>1.2. Universitet ichki taʼlim sifatini taʼminlash tizimi strategik menejmentning ajralmas qismi boʻlib, boshqa hujjatlar, masalan, Universitetning rivojlanish konsepsiyasi, strategiyasi, akademik siyosati bilan birgalikda koʻrib chiqiladi.</p>`
        }
    ];

    const initialIntro = "Renessans Ta'lim Universiteti ta'lim sifatini doimiy ravishda oshirib borish, xalqaro standartlarga muvofiqlikni ta'minlash va talabalarga yo'naltirilgan ta'lim muhitini yaratishga intiladi.";

    const fetchData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${testUrl}/api/quality-policy/`);

            if (response.data) {
                const { sections, intro, presentations } = response.data;
                const lang = i18n.language;

                if (sections && sections.length > 0) {
                    const mappedData = sections.map(item => ({
                        ...item, // Keep all fields including translations
                        title: item[`title_${lang}`] || item.title_uz,
                        content: item[`content_${lang}`] || item.content_uz
                    }));
                    setPolicyData(mappedData);
                } else {
                    setPolicyData(initialSections);
                }

                if (intro) {
                    setIntroData({
                        ...intro,
                        content: intro[`content_${lang}`] || intro.content_uz,
                        content_uz: intro.content_uz
                    });
                } else {
                    setIntroData({ content: initialIntro, content_uz: initialIntro });
                }

                if (presentations) {
                    setPresentations(presentations);
                }
            }
        } catch (error) {
            console.error("Error fetching quality policy:", error);
            setPolicyData(initialSections);
            setIntroData({ content: initialIntro, content_uz: initialIntro });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [i18n.language]);


    const toggleAccordion = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    const handleSave = async () => {
        try {
            const authToken = localStorage.getItem("authToken");
            const payload = {
                sections: policyData.map(item => ({
                    ...item,
                    // If we are editing specifically title and content in the UI, 
                    // ensure we map them back to the state fields expected by the backend
                    title_uz: item.title_uz || item.title,
                    content_uz: item.content_uz || item.content,
                })),
                intro: {
                    ...introData,
                    content_uz: introData.content_uz
                }
            };
            const headers = authToken ? { Authorization: `Bearer ${authToken}` } : {};
            await axios.post(`${testUrl}/api/quality-policy/bulk-sync/`, payload, { headers });
            setIsEditModalOpen(false);
            alert("Ma'lumotlar global miqyosda saqlandi!");
            fetchData();
        } catch (error) {
            console.error("Error saving quality policy:", error);
            alert("Xatolik yuz berdi. Iltimos qaytadan urunib ko'ring.");
        }
    };

    const addNewSection = () => {
        setPolicyData([...policyData, { title: "Yangi bo'lim", content: "" }]);
    };

    const deleteSection = (index) => {
        if (window.confirm("Haqiqatan ham ushbu bo'limni o'chirib tashlamoqchimisiz?")) {
            const newData = policyData.filter((_, i) => i !== index);
            setPolicyData(newData);
        }
    };

    const updateSection = (index, field, value) => {
        const newData = [...policyData];
        newData[index][field] = value;
        setPolicyData(newData);
    };

    const updateIntro = (value) => {
        setIntroData({ ...introData, content: value, content_uz: value });
    };

    if (loading) {
        return <div className={styles.loading}>{t("loading") || "Yuklanmoqda..."}</div>;
    }

    return (
        <div className={styles.mainContainer}>
            <div className={styles.container}>
                <h1 className={styles.mainTitle} data-aos="fade-up">
                    Universitetning ichki taʼlim sifatini <br /> taʼminlash siyosati
                </h1>

                {/* Decorative Divider */}
                <div className={styles.premiumDivider} data-aos="fade-up">
                    <div className={styles.dividerLine}></div>
                    <div className={styles.dividerIcon}>
                        <FaAward />
                    </div>
                    <div className={styles.dividerLine}></div>
                </div>

                <div className={styles.introBox} data-aos="fade-up">
                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(introData.content || initialIntro, { ADD_ATTR: ['style', 'target'] }) }} />
                </div>

                <div className={styles.accordionContainer} data-aos="fade-up">
                    {policyData.map((item, index) => (
                        <div key={index} className={`${styles.accordionItem} ${activeIndex === index ? styles.active : ""}`}>
                            <button className={styles.accordionHeader} onClick={() => toggleAccordion(index)}>
                                <div className={styles.headerTitle}>
                                    <span className={styles.icon}>
                                        <FaShieldAlt />
                                    </span>
                                    <h2>{item.title}</h2>
                                </div>
                                <div className={styles.headerAction}>
                                    <span className={styles.seeMoreTxt}>{activeIndex === index ? "Yopish" : "Ko'proq ko'rish"}</span>
                                    {activeIndex === index ? <FaChevronUp /> : <FaChevronDown />}
                                </div>
                            </button>
                            <div className={styles.accordionContent}>
                                <div className={styles.innerContent}>
                                    <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.content, { ADD_ATTR: ['style', 'target'] }) }} />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* SIFAT SIYOSATI KO'RGAZMALI KARTOCHKALARI */}
                <div className={styles.cardsGrid} data-aos="fade-up">
                    {/* CARD 1: Asosiy tushunchalar */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.navyStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.navyLight}`}>📖</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.navyText}`}>I Bo'lim</div>
                                <div className={styles.cardTitle}>Asosiy Tushunchalar</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <ul className={styles.itemList}>
                                <li>
                                    <span className={`${styles.dot} ${styles.navyDot}`}>1</span>
                                    <div><strong>Ta'lim sifati</strong>Ishtirokchilar qoniqishi, mazmunning manfaatdorlar ehtiyojlariga muvofiqligi va belgilangan natijalarga erishish darajasi.</div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.blueDot}`}>2</span>
                                    <div><strong>Sifatni ta'minlash</strong>Belgilangan sifat darajasiga muvofiqligini kafolatlovchi shart-sharoitlarni yaratish va resurslar ajratish jarayoni.</div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.tealDot}`}>3</span>
                                    <div><strong>Sifat madaniyati</strong>Har bir xodim sifat uchun shaxsan javobgar — tashkiliy muhitda ustuvor qadriyat sifatida qabul qilinadi.</div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.goldDot}`}>4</span>
                                    <div><strong>Manfaatdorlar</strong>Ichki (talabalar, o'qituvchilar) va tashqi (ish beruvchilar, bitiruvchilar, hamkorlar) manfaatdor tomonlar.</div>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* CARD 2: 11 Tamoyil */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.tealStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.tealLight}`}>⚖️</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.tealText}`}>II Bo'lim</div>
                                <div className={styles.cardTitle}>Sifatni Ta'minlash — 11 Tamoyil</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.miniGrid}>
                                <div className={`${styles.miniCard} ${styles.navyLight}`}><strong>🎯 Strategik muvofiqlik</strong>2030 konsepsiyasi bilan uyg'un</div>
                                <div className={`${styles.miniCard} ${styles.blueLight}`}><strong>👥 Manfaatdorlarga yo'nalish</strong>Ichki va tashqi ehtiyojlar</div>
                                <div className={`${styles.miniCard} ${styles.tealLight}`}><strong>🔍 Shaffoflik</strong>Ochiq va adolatli baholash</div>
                                <div className={`${styles.miniCard} ${styles.goldLight}`}><strong>🔄 Uzluksiz takomil</strong>Monitoring asosida yaxshilanish</div>
                                <div className={`${styles.miniCard} ${styles.purpleLight}`}><strong>📊 Natijaviylik</strong>Aniq mezonlar orqali baholash</div>
                                <div className={`${styles.miniCard} ${styles.orangeLight}`}><strong>💡 Innovatsiya</strong>Raqamli va ilg'or texnologiyalar</div>
                                <div className={`${styles.miniCard} ${styles.greenLight}`}><strong>🌐 Xalqaro standartlar</strong>ESG talablariga muvofiqlik</div>
                                <div className={`${styles.miniCard} ${styles.navyLight}`}><strong>✅ Javobgarlik</strong>Har bir bo'linma uchun aniq</div>
                            </div>
                        </div>
                    </div>

                    {/* CARD 3: Javobgarlik */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.blueStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.blueLight}`}>🏛️</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.blueText}`}>III Bo'lim</div>
                                <div className={styles.cardTitle}>Sifatni Ta'minlash Uchun Javobgarlik Tuzilmasi</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                                <div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.navyBg}`}></div>
                                        <div className={styles.roleContent}><strong>Rahbariyat</strong><span>Sifat siyosatini tasdiqlaydi, resurslarni ajratadi, nazorat qiladi</span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.blueBg}`}></div>
                                        <div className={styles.roleContent}><strong>Prorektorlar / Bo'linmalar</strong><span>Strategik ko'rsatkichlar ijrosi uchun javobgar</span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.tealBg}`}></div>
                                        <div className={styles.roleContent}><strong>Fakultet va Kafedralar</strong><span>Ta'lim dasturlari sifati, xolis baholash, mehnat bozori moslik</span></div>
                                    </div>
                                </div>
                                <div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.purpleBg}`}></div>
                                        <div className={styles.roleContent}><strong>Sifat Bo'linmasi</strong><span>Monitoring, ichki audit, tahlil, sifat madaniyatini rivojlantirish</span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.orangeBg}`}></div>
                                        <div className={styles.roleContent}><strong>Professor-o'qituvchilar</strong><span>Shaxsiy javobgarlik — akademik halollik va professional etika</span></div>
                                    </div>
                                    <div className={styles.roleRow}>
                                        <div className={`${styles.roleBar} ${styles.greenBg}`}></div>
                                        <div className={styles.roleContent}><strong>Talabalar</strong><span>Faol ishtirok, halollik qoidalariga rioya, fikr-mulohaza bildirish</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* CARD 4: SY1 Sifat Siyosati */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.goldStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.goldLight}`}>📋</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.goldText}`}>IV Bo'lim — SY1 (ESG-1.1, 1.10)</div>
                                <div className={styles.cardTitle}>Sifat Siyosati va Davriy Baholash</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={`${styles.highlightInfo} ${styles.navyLight}`} style={{ marginBottom: "16px" }}>
                                <strong style={{ color: "var(--navy)" }}>Sifat siyosati jamoatchilik uchun ochiq</strong> bo'lib, 2030-yilgacha rivojlanish konsepsiyasi va strategik reja bilan uzviy bog'liq.
                            </div>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>Siyosat Maqsadlari</p>
                            <ul className={styles.itemList}>
                                <li><span className={`${styles.dot} ${styles.navyDot}`}>✓</span><div>Ichki sifat tizimining tashkiliy tuzilmasini belgilash</div></li>
                                <li><span className={`${styles.dot} ${styles.navyDot}`}>✓</span><div>Jamoa orasida sifat madaniyatini shakllantirish</div></li>
                                <li><span className={`${styles.dot} ${styles.navyDot}`}>✓</span><div>Ta'lim sifati va natijadorligini doimiy takomillashtirish</div></li>
                                <li><span className={`${styles.dot} ${styles.navyDot}`}>✓</span><div>Manfaatdorlar uchun shaffof va ishonchli axborot taqdim etish</div></li>
                            </ul>
                        </div>
                    </div>

                    {/* CARD 5: SY2 Ta'lim Dasturlari */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.purpleStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.purpleLight}`}>🔄</div>
                            <div className={styles.cardTitleArea}>
                                <div className={styles.cardSection} style={{ color: "var(--purple)" }}>V Bo'lim — SY2 (ESG-1.2, 1.9)</div>
                                <div className={styles.cardTitle}>Ta'lim Dasturlarini Ishlab Chiqish</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "12px" }}>PDCA Tsikli</p>
                            <ul className={styles.itemList}>
                                <li>
                                    <span className={`${styles.dot} ${styles.navyDot}`}>1</span>
                                    <div><strong>Rejalashtirish</strong>Manfaatdorlar tahlili, kasbiy standartlar, mehnat bozori talablari asosida maqsadlarni belgilash</div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.blueDot}`}>2</span>
                                    <div><strong>Amalga oshirish</strong>Tasdiqlangan o'quv reja va syllabuslar asosida o'quv jarayonini tashkil etish</div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.tealDot}`}>3</span>
                                    <div><strong>Monitoring</strong>Talabalar natijalari, so'rovnomalar va tashqi ko'rib chiqishlar orqali kuzatish</div>
                                </li>
                                <li>
                                    <span className={`${styles.dot} ${styles.purpleDot}`}>4</span>
                                    <div><strong>Takomillashtirish</strong>Natijalar asosida dastur mazmuni, o'qitish usullari va baholash tizimini yangilash</div>
                                </li>
                            </ul>
                        </div>
                    </div>

                    {/* CARD 6: SY3 Qabul */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.tealStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.tealLight}`}>🎓</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.tealText}`}>VI Bo'lim — SY3 (ESG-1.3, 1.4)</div>
                                <div className={styles.cardTitle}>Qabul va Talabaga Yo'naltirilgan Ta'lim</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={`${styles.highlightInfo} ${styles.tealLight}`} style={{ marginBottom: "14px" }}>
                                <strong>Qabul jarayoni:</strong> Shaffof mezonlar, xalqaro va milliy akkreditatsiya standartlariga rioya, ochiq axborot.
                            </div>
                            <div className={`${styles.highlightInfo} ${styles.blueLight}`}>
                                <strong>Talabaga yo'naltirilganlik:</strong> Individual ta'lim yo'nalishi, tyutorlik tizimi, psixologik va ijtimoiy ko'mak xizmatlari.
                            </div>
                            <div className={styles.badgeRow} style={{ marginTop: "14px" }}>
                                <span className={`${styles.badgeItem} ${styles.tealLight}`}>Tyutorlik tizimi</span>
                                <span className={`${styles.badgeItem} ${styles.blueLight}`}>Psixologik yordam</span>
                                <span className={`${styles.badgeItem} ${styles.navyLight}`}>Ijtimoiy qo'llab-quvvatlash</span>
                                <span className={`${styles.badgeItem} ${styles.tealLight}`}>Diplom berish shaffofligi</span>
                            </div>
                        </div>
                    </div>

                    {/* CARD 7: SY4 Professor-o'qituvchilar */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.orangeStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.orangeLight}`}>👨‍🏫</div>
                            <div className={styles.cardTitleArea}>
                                <div className={styles.cardSection} style={{ color: "var(--orange)" }}>VII Bo'lim — SY4 (ESG-1.5, 1.6)</div>
                                <div className={styles.cardTitle}>Professor-o'qituvchilar va Resurslar</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>Kadrlar Siyosati</p>
                            <ul className={styles.itemList} style={{ marginBottom: "16px" }}>
                                <li><span className={`${styles.dot} ${styles.orangeDot}`}>✓</span><div>Tanlov asosida shaffof ishga qabul (14 kun avval e'lon)</div></li>
                                <li><span className={`${styles.dot} ${styles.orangeDot}`}>✓</span><div>Har 5 yilda malaka oshirish rejasi amalga oshiriladi</div></li>
                                <li><span className={`${styles.dot} ${styles.orangeDot}`}>✓</span><div>Ilmiy faoliyatni qo'llab-quvvatlash: konferensiyalar, nashrlar</div></li>
                                <li><span className={`${styles.dot} ${styles.orangeDot}`}>✓</span><div>KPI tizimi orqali samaradorlikni baholash</div></li>
                            </ul>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>Ta'lim Resurslari</p>
                            <div className={styles.badgeRow}>
                                <span className={`${styles.badgeItem} ${styles.navyLight}`}>🏫 Infratuzilma</span>
                                <span className={`${styles.badgeItem} ${styles.blueLight}`}>📚 Kutubxona</span>
                                <span className={`${styles.badgeItem} ${styles.tealLight}`}>💻 LMS tizimi</span>
                                <span className={`${styles.badgeItem} ${styles.purpleLight}`}>🧑‍⚕️ Tibbiyot xizmati</span>
                                <span className={`${styles.badgeItem} ${styles.orangeLight}`}>🏋️ Sport majmuasi</span>
                            </div>
                        </div>
                    </div>

                    {/* CARD 8: SY5 Axborot */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.blueStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.blueLight}`}>📡</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.blueText}`}>VIII Bo'lim — SY5 (ESG-1.7, 1.8)</div>
                                <div className={styles.cardTitle}>Axborotni Boshqarish Siyosati</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>Asosiy Axborot Tizimlari</p>
                            <div className={styles.miniGrid} style={{ marginBottom: "14px" }}>
                                <div className={`${styles.miniCard} ${styles.navyLight}`}><strong>HEMIS</strong>Asosiy boshqaruv tizimi</div>
                                <div className={`${styles.miniCard} ${styles.blueLight}`}><strong>LMS Portal</strong>Masofaviy ta'lim</div>
                                <div className={`${styles.miniCard} ${styles.tealLight}`}><strong>KPI Platforma</strong>Samaradorlik baholash</div>
                                <div className={`${styles.miniCard} ${styles.purpleLight}`}><strong>Monitoring tizimi</strong>Test va nazorat</div>
                            </div>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>Yillik So'rovnomalar</p>
                            <ul className={styles.itemList}>
                                <li><span className={`${styles.dotIcon} ${styles.blueText}`}>📋</span><div>Talabalar — o'qitish sifati bo'yicha</div></li>
                                <li><span className={`${styles.dotIcon} ${styles.blueText}`}>📋</span><div>Professor-o'qituvchilar — o'quv jarayoni bo'yicha</div></li>
                                <li><span className={`${styles.dotIcon} ${styles.blueText}`}>📋</span><div>Bitiruvchilar — ta'lim xizmatlari bo'yicha</div></li>
                                <li><span className={`${styles.dotIcon} ${styles.blueText}`}>📋</span><div>Ish beruvchilar — bitiruvchilar tayyorligi bo'yicha</div></li>
                            </ul>
                        </div>
                    </div>

                    {/* CARD 9: Veb-sayt axborot siyosati */}
                    <div className={styles.card}>
                        <div className={`${styles.cardStrip} ${styles.greenStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.greenLight}`}>🌐</div>
                            <div className={styles.cardTitleArea}>
                                <div className={styles.cardSection} style={{ color: "var(--green)" }}>VIII Bo'lim — Axborot Ochiqligi</div>
                                <div className={styles.cardTitle}>Veb-sayt va Ommaviy Axborot</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={`${styles.highlightInfo} ${styles.greenLight}`} style={{ marginBottom: "14px" }}>
                                Taqdim etiladigan axborot <strong>aniq, ravshan, xolis, dolzarb va ochiq</strong> bo'lishi kafolatlanadi.
                            </div>
                            <p style={{ fontSize: "12.5px", fontWeight: "700", color: "var(--gray)", letterSpacing: "1px", textTransform: "uppercase", marginBottom: "10px" }}>Veb-sayt Bo'limlari</p>
                            <div className={styles.badgeRow}>
                                <span className={`${styles.badgeItem} ${styles.navyLight}`}>Universitet</span>
                                <span className={`${styles.badgeItem} ${styles.blueLight}`}>Faoliyat</span>
                                <span className={`${styles.badgeItem} ${styles.tealLight}`}>Hamkorlik</span>
                                <span className={`${styles.badgeItem} ${styles.greenLight}`}>Talabalarga</span>
                                <span className={`${styles.badgeItem} ${styles.purpleLight}`}>Qabul</span>
                                <span className={`${styles.badgeItem} ${styles.orangeLight}`}>Axborot xizmati</span>
                            </div>
                            <div style={{ marginTop: "14px", padding: "12px", background: "var(--light)", borderRadius: "10px", fontSize: "12.5px", color: "var(--gray)" }}>
                                📣 Rektor har yili jamoatchilik oldida faoliyat natijalari to'g'risida hisobot beradi.
                            </div>
                        </div>
                    </div>

                    {/* CARD 10: Yakuniy - statistika */}
                    <div className={`${styles.card} ${styles.wideCard}`}>
                        <div className={`${styles.cardStrip} ${styles.navyStrip}`}></div>
                        <div className={styles.cardHeader}>
                            <div className={`${styles.cardIcon} ${styles.navyLight}`}>🏆</div>
                            <div className={styles.cardTitleArea}>
                                <div className={`${styles.cardSection} ${styles.navyText}`}>IX Bo'lim — Yakuniy Qism</div>
                                <div className={styles.cardTitle}>Siyosatning Asosiy Ko'rsatkichlari</div>
                            </div>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.statRow} style={{ marginBottom: "20px" }}>
                                <div className={`${styles.statBox} ${styles.navyLight}`}>
                                    <div className={`${styles.statNum} ${styles.navyText}`}>11</div>
                                    <div className={`${styles.statLabel} ${styles.navyText}`}>Sifatni ta'minlash tamoyili</div>
                                </div>
                                <div className={`${styles.statBox} ${styles.blueLight}`}>
                                    <div className={`${styles.statNum} ${styles.blueText}`}>5</div>
                                    <div className={`${styles.statLabel} ${styles.blueText}`}>Asosiy siyosat yo'nalishlari (SY1–SY5)</div>
                                </div>
                                <div className={`${styles.statBox} ${styles.tealLight}`}>
                                    <div className={`${styles.statNum} ${styles.tealText}`}>4</div>
                                    <div className={`${styles.statLabel} ${styles.tealText}`}>Yillik so'rovnoma guruhlari</div>
                                </div>
                                <div className={`${styles.statBox} ${styles.goldLight}`}>
                                    <div className={`${styles.statNum} ${styles.goldText}`}>5</div>
                                    <div className={`${styles.statLabel} ${styles.goldText}`}>Har 5 yilda malaka oshirish</div>
                                </div>
                                <div className={`${styles.statBox} ${styles.purpleLight}`}>
                                    <div className={styles.statNum} style={{ color: "var(--purple)" }}>18</div>
                                    <div className={styles.statLabel} style={{ color: "var(--purple)" }}>Hujjat sahifalari</div>
                                </div>
                            </div>
                            <div style={{ background: "var(--navy)", borderRadius: "12px", padding: "18px 22px", color: "var(--white)" }}>
                                <strong style={{ display: "block", fontSize: "15px", marginBottom: "8px" }}>📌 Muhim eslatma</strong>
                                <span style={{ fontSize: "13.5px", opacity: 0.9, lineHeight: 1.6 }}>
                                    Ushbu siyosat barcha professor-o'qituvchilar, ma'muriy xodimlar va fakultetlar tomonidan izchil amalga oshiriladi.
                                    O'zgartirishlar faqat <strong>Universitet Kengashining qaroriga</strong> asosan kiritiladi.
                                </span>
                            </div>
                        </div>
                    </div>

                </div>

                {/* SCROLLING IMAGES SECTION */}
                <div style={{ marginTop: "60px", width: "100%" }} data-aos="fade-up">
                    <Swiper
                        modules={[Autoplay, Pagination, Navigation]}
                        spaceBetween={30}
                        slidesPerView={1}
                        breakpoints={{
                            640: { slidesPerView: 1 },
                            768: { slidesPerView: 2 },
                            1024: { slidesPerView: 2.5 },
                        }}
                        autoplay={{ delay: 2500, disableOnInteraction: false }}
                        pagination={{ clickable: true }}
                        navigation={true}
                        style={{ paddingBottom: "60px", paddingLeft: "10px", paddingRight: "10px" }}
                    >
                        {presentations && presentations.length > 0 ? (
                            presentations.map((item, index) => (
                                <SwiperSlide key={index}>
                                    <div style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: "10px 0"
                                    }}>
                                        <img
                                            src={item.image}
                                            alt={`Taqdimot ${index + 1}`}
                                            style={{
                                                width: "100%",
                                                height: "auto",
                                                objectFit: "contain",
                                                cursor: "pointer",
                                                borderRadius: "16px",
                                                boxShadow: "0 4px 15px rgba(0,0,0,0.12)"
                                            }}
                                            onClick={() => setSelectedImage(item.image)}
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=Rasm+Topilmadi'; }}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))
                        ) : (
                            [1, 2, 3, 4].map((item, index) => (
                                <SwiperSlide key={index}>
                                    <div style={{
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center",
                                        padding: "10px 0"
                                    }}>
                                        <img
                                            src={`https://picsum.photos/seed/${item * 10}/800/400`}
                                            alt={`Namuna ${item}`}
                                            style={{
                                                width: "100%",
                                                height: "auto",
                                                objectFit: "cover",
                                                cursor: "pointer",
                                                borderRadius: "16px",
                                                boxShadow: "0 4px 15px rgba(0,0,0,0.12)"
                                            }}
                                            onClick={() => setSelectedImage(`https://picsum.photos/seed/${item * 10}/800/400`)}
                                            onError={(e) => { e.target.src = 'https://via.placeholder.com/800x400?text=Rasm+Topilmadi'; }}
                                        />
                                    </div>
                                </SwiperSlide>
                            ))
                        )}
                    </Swiper>
                </div>
            </div>

            {/* Admin Edit Modal */}
            {isEditModalOpen && (
                <div className={styles.modalOverlay}>
                    <div className={styles.modalContent}>
                        <div className={styles.modalHeader}>
                            <h2>Siyosatni tahrirlash (Rich Text)</h2>
                            <button className={styles.closeBtn} onClick={() => setIsEditModalOpen(false)}>&times;</button>
                        </div>

                        <div className={styles.formList}>
                            <div className={styles.formItem} style={{ borderBottom: '2px solid #eee', paddingBottom: '20px', marginBottom: '20px' }}>
                                <div className={styles.inputGroup}>
                                    <label style={{ fontWeight: 'bold', color: '#003366' }}>Kirish matni (Rich Text)</label>
                                    <ReactQuill
                                        theme="snow"
                                        value={introData.content_uz}
                                        onChange={updateIntro}
                                        modules={quillModules}
                                    />
                                </div>
                            </div>

                            <label style={{ fontWeight: 'bold', color: '#003366', display: 'block', marginBottom: '10px' }}>Bo'limlar</label>

                            {policyData.map((item, index) => (
                                <div key={index} className={styles.formItem}>
                                    <div className={styles.inputGroup}>
                                        <label>Sarlavha</label>
                                        <input
                                            className={styles.inputField}
                                            value={item.title}
                                            onChange={(e) => updateSection(index, 'title', e.target.value)}
                                        />
                                    </div>
                                    <div className={styles.inputGroup}>
                                        <label>Mazmun (Rich Text)</label>
                                        <ReactQuill
                                            theme="snow"
                                            value={item.content}
                                            onChange={(val) => updateSection(index, 'content', val)}
                                            modules={quillModules}
                                        />
                                    </div>
                                    <div className={styles.itemActions}>
                                        <button className={styles.deleteBtn} onClick={() => deleteSection(index)}>
                                            <FaTrash style={{ marginRight: '5px' }} /> O'chirish
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <button className={styles.addBtn} onClick={addNewSection}>
                            <FaPlus /> Yangi bo'lim qo'shish
                        </button>

                        <div className={styles.footerActions}>
                            <button className={styles.saveBtn} onClick={handleSave}>O'zgarishlarni saqlash</button>
                        </div>
                    </div>
                </div>
            )}

            {/* FULL SCREEN IMAGE MODAL */}
            {selectedImage && (
                <div
                    style={{
                        position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                        backgroundColor: "rgba(0,0,0,0.9)", zIndex: 9999,
                        display: "flex", justifyContent: "center", alignItems: "center", cursor: "zoom-out"
                    }}
                    onClick={() => setSelectedImage(null)}
                >
                    <img
                        src={selectedImage}
                        style={{ maxWidth: "90%", maxHeight: "90%", objectFit: "contain" }}
                        alt="Kattalashtirilgan rasm"
                    />
                    <button
                        style={{
                            position: "absolute", top: "20px", right: "30px", background: "transparent",
                            color: "white", border: "none", fontSize: "40px", cursor: "pointer"
                        }}
                        onClick={() => setSelectedImage(null)}
                    >
                        &times;
                    </button>
                </div>
            )}
        </div>
    );
};

export default SifatSiyosati;
